/**
 * AI Chat Widget — full ordering flow
 * Floating FAB → responsive chat panel.
 * Features: item suggestion cards, delivery/collection picker,
 *           address selector, inline cart panel with amend/notes/delivery instructions,
 *           inline modifier picker (ModifierCard) for items with options.
 */

import {
  Close as CloseIcon,
  DeleteSweep as ClearIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Send as SendIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  Dialog,
  Fab,
  IconButton,
  Slide,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { chatApi } from '@/services/api';
import { useAppStore } from '@/store/useAppStore';
import { tn } from '@/utils/i18n';
import { useCartStore } from '@/store/useCartStore';
import { OrderType, type CartModifier } from '@/types';
import ChatCartPanel from './ChatCartPanel';
import ChatMessage, { type ChatMessageData, type SuggestedItem } from './ChatMessage';
import { TypingIndicator } from './ChatMessage';
import ModifierCard, { type OptionGroup, type PickerOption, type PendingModifierItem } from './ModifierCard';

// ─── Web Speech API type shim ─────────────────────────────────────────────────

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  }
}
interface SpeechRecognition extends EventTarget {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  start(): void;
  stop(): void;
}
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  readonly length: number;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

// ─── Slide-up transition for desktop panel ────────────────────────────────────

const SlideUp = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});


function makeId() {
  return Math.random().toString(36).slice(2, 10);
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChatWidget() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const language = useAppStore((state) => state.language);
  const setLanguage = useAppStore((state) => state.setLanguage);
  const restaurant = useAppStore((state) => state.restaurant);
  // Cart store
  const addItem = useCartStore((state) => state.addItem);
  const updateQuantityByMenuItemId = useCartStore((state) => state.updateQuantityByMenuItemId);
  const clearCart = useCartStore((state) => state.clearCart);
  const cartItems = useCartStore((state) => state.items);
  const setOrderType = useCartStore((state) => state.setOrderType);

  // Chat UI state
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [greeted, setGreeted] = useState(false);
  const [showCallout, setShowCallout] = useState(false);

  // Queue of pending modifier items — shown one at a time as inline ModifierCard
  const [pendingModifierQueue, setPendingModifierQueue] = useState<PendingModifierItem[]>([]);

  // Track which message IDs have had their interaction "consumed" (no re-trigger)
  const [consumedInteractions, setConsumedInteractions] = useState<Set<string>>(new Set());

  // Track previous cart length to detect cart-cleared events
  const prevCartLengthRef = useRef(cartItems.length);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const speechSupported =
    typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const isAdmin = location.pathname.startsWith('/admin');

  // First-time callout bubble — shows once after 2.5s, auto-dismisses after 5s
  useEffect(() => {
    if (open || localStorage.getItem('chat_callout_seen')) return;
    const show = setTimeout(() => setShowCallout(true), 2500);
    return () => clearTimeout(show);
  }, [open]);

  const dismissCallout = () => {
    setShowCallout(false);
    localStorage.setItem('chat_callout_seen', '1');
  };

  // Auto-scroll on new messages
  useEffect(() => {
    if (open) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [messages, open, loading, pendingModifierQueue]);

  // Greeting on first open
  useEffect(() => {
    if (open && !greeted) {
      setGreeted(true);
      setMessages([
        {
          id: makeId(),
          role: 'model',
          content: t('chat.greeting'),
          timestamp: new Date(),
        },
      ]);
    }
  }, [open, greeted, t]);

  // Reset unread count when opened
  useEffect(() => {
    if (open) setUnreadCount(0);
  }, [open]);

  // Cart cleared → reset chat to greeting
  useEffect(() => {
    if (prevCartLengthRef.current > 0 && cartItems.length === 0) {
      setMessages([
        {
          id: makeId(),
          role: 'model',
          content: t('chat.greeting'),
          timestamp: new Date(),
        },
      ]);
      setPendingModifierQueue([]);
      setConsumedInteractions(new Set());
    }
    prevCartLengthRef.current = cartItems.length;
  }, [cartItems.length, t]);

  // Clear chat history and restart greeting
  const handleClearChat = useCallback(() => {
    setMessages([]);
    setPendingModifierQueue([]);
    setConsumedInteractions(new Set());
    setGreeted(false);
  }, []);

  // ─── Inject a synthetic AI message (no API call) ─────────────────────────────

  const injectAIMessage = useCallback(
    (content: string, extras: Partial<ChatMessageData> = {}) => {
      const msg: ChatMessageData = {
        id: makeId(),
        role: 'model',
        content,
        timestamp: new Date(),
        ...extras,
      };
      setMessages((prev) => [...prev, msg]);
      if (!open) setUnreadCount((c) => c + 1);
      return msg.id;
    },
    [open]
  );

  // ─── Cart checkout button handler ───────────────────────────────────────────

  const handleCartCheckout = useCallback(() => {
    injectAIMessage(
      language === 'ar'
        ? 'بالطبع! هل تريد التوصيل أم الاستلام؟'
        : 'Sure! Would you like delivery or collection?',
      { showOrderTypeSelector: true }
    );
  }, [language, injectAIMessage]);

  // ─── Order type selection handler ────────────────────────────────────────────

  const handleOrderTypeSelect = useCallback(
    (type: 'delivery' | 'collection') => {
      const orderTypeEnum = type === 'delivery' ? OrderType.DELIVERY : OrderType.COLLECTION;
      setOrderType(orderTypeEnum);

      // Consume the selector so it dims
      setConsumedInteractions((prev) => {
        const next = new Set(prev);
        const msgId = [...messages].reverse().find((m) => m.showOrderTypeSelector)?.id;
        if (msgId) next.add(msgId);
        return next;
      });

      setOpen(false);
      navigate('/checkout');
    },
    [messages, setOrderType, navigate]
  );

  // ─── Modifier confirm handler ─────────────────────────────────────────────

  const handleModifiersConfirm = useCallback(
    (selections: PickerOption[]) => {
      const pending = pendingModifierQueue[0];
      if (!pending) return;

      addItem({
        menu_item_id: pending.pendingItem.menu_item_id,
        name: pending.pendingItem.name,
        unit_price: pending.pendingItem.unit_price,
        quantity: pending.quantity,
        modifiers: selections.map((s) => ({
          group_id: s.group_id,
          option_id: s.option_id,
          name: s.label,
          price_adjustment: s.price_adjustment ?? 0,
        })) as CartModifier[],
      });

      const confirmMsg =
        language === 'ar'
          ? `تمت إضافة ${pending.pendingItem.name} ✓`
          : `${pending.pendingItem.name} added ✓`;
      injectAIMessage(confirmMsg);

      setPendingModifierQueue((prev) => prev.slice(1));
    },
    [pendingModifierQueue, addItem, language, injectAIMessage]
  );

  // ─── Process actions returned by AI ─────────────────────────────────────────

  const processActions = useCallback(
    (actions: Array<{ type: string; [key: string]: unknown }>): Partial<ChatMessageData> => {
      const result: Partial<ChatMessageData> = {};

      for (const action of actions) {
        switch (action.type) {
          case 'add_to_cart': {
            try {
              addItem({
                menu_item_id: action.menu_item_id as string,
                name: action.name as string,
                unit_price: action.unit_price as number,
                quantity: (action.quantity as number) || 1,
                modifiers: [],
              });
            } catch {
              // ignore cart errors
            }
            break;
          }

          case 'show_items': {
            result.suggestedItems = action.items as SuggestedItem[];
            result.hasMoreItems = !!(action.has_more as boolean);
            result.suggestedItemsTitle = action.title as string | undefined;
            break;
          }

          case 'show_options': {
            const groups = action.groups as OptionGroup[];
            const pendingItem = action.pending_item as { menu_item_id: string; name: string; unit_price: number };
            const quantity = (action.quantity as number) ?? 1;
            if (groups?.length && pendingItem) {
              setPendingModifierQueue((prev) => [...prev, { groups, pendingItem, quantity }]);
            }
            break;
          }

          case 'update_cart': {
            // Use the store action that performs its own fresh-state lookup
            // to avoid stale-closure issues with cartItems.find()
            updateQuantityByMenuItemId(
              action.menu_item_id as string,
              action.quantity as number,
            );
            break;
          }

          case 'clear_cart': {
            clearCart();
            break;
          }

          case 'checkout': {
            handleCartCheckout();
            break;
          }
        }
      }

      return result;
    },
    [addItem, updateQuantityByMenuItemId, clearCart, handleCartCheckout]
  );

  // ─── "Show more items" — sends a message to AI asking for more ───────────────

  const handleShowMoreItems = useCallback(() => {
    sendMessage(language === 'ar' ? 'أرني المزيد من العناصر' : 'Show me more items');
  }, [language]); // eslint-disable-line

  // ─── Send message ─────────────────────────────────────────────────────────────

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || loading) return;
      const userMsg: ChatMessageData = {
        id: makeId(),
        role: 'user',
        content: text.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setLoading(true);

      // History: last 15 messages only
      const history = messages.slice(-15).map((m) => ({ role: m.role, content: m.content }));

      try {
        const cartPayload = cartItems.map((i) => ({
          menu_item_id: i.menu_item_id,
          name: i.name,
          quantity: i.quantity,
        }));
        const res = await chatApi.sendMessage(text.trim(), history, language, undefined, cartPayload);
        const data = res.data;

        const msgExtras = processActions(data.actions || []);

        const aiMsg: ChatMessageData = {
          id: makeId(),
          role: 'model',
          content: data.message,
          timestamp: new Date(),
          ...msgExtras,
        };

        setMessages((prev) => [...prev, aiMsg]);
        if (!open) setUnreadCount((c) => c + 1);
      } catch (err: unknown) {
        const errMsg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (language === 'ar'
            ? 'مساعد الدردشة مشغول الآن. يرجى المحاولة مرة أخرى.'
            : 'Our chat assistant is busy. Please try again in a moment!');

        setMessages((prev) => [
          ...prev,
          {
            id: makeId(),
            role: 'model',
            content: errMsg,
            timestamp: new Date(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [loading, messages, language, processActions, open, cartItems]
  );

  // ─── Voice input ──────────────────────────────────────────────────────────────

  const startVoiceInput = () => {
    if (!speechSupported) return;
    const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;

    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = language === 'ar' ? 'ar-SA' : 'en-GB';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript ?? '';
      if (transcript) sendMessage(transcript);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  // Don't render on admin pages — after all hooks
  if (isAdmin) return null;

  // ─── Panel Content ────────────────────────────────────────────────────────────

  const panelContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? '100dvh' : '90vh',
        width: '100%',
        bgcolor: 'background.paper',
        borderRadius: isMobile ? 0 : 3,
        overflow: 'hidden',
        boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0,0,0,0.2)',
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', width: 36, height: 36 }}>
          <SparkleIcon sx={{ fontSize: 20, color: 'white' }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ color: 'white', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {tn(restaurant?.name_translations) || t('chat.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4CAF50' }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem' }}>
              {t('chat.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Language toggle */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {(['en', 'ar'] as const).map((lang) => (
            <Box
              key={lang}
              onClick={() => setLanguage(lang)}
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: language === lang ? 'rgba(255,255,255,0.3)' : 'transparent',
                color: 'white',
                fontSize: '0.7rem',
                fontWeight: 700,
                transition: 'background 0.15s',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              }}
            >
              {lang === 'en' ? 'EN' : 'ع'}
            </Box>
          ))}
        </Box>

        <IconButton
          size="small"
          onClick={handleClearChat}
          title={language === 'ar' ? 'مسح المحادثة' : 'Clear chat'}
          sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>

        <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Messages ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : '#F8F8F8',
        }}
      >
        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            language={language}
            onOrderTypeSelect={
              msg.showOrderTypeSelector && !consumedInteractions.has(msg.id)
                ? handleOrderTypeSelect
                : undefined
            }
            onShowMoreItems={handleShowMoreItems}
            onAddWithModifiers={(itemName) => sendMessage(`Add ${itemName}`)}
            interactionDisabled={consumedInteractions.has(msg.id)}
          />
        ))}

        {/* Inline modifier picker — first item in queue */}
        {pendingModifierQueue[0] && (
          <ModifierCard
            groups={pendingModifierQueue[0].groups}
            itemName={pendingModifierQueue[0].pendingItem.name}
            quantity={pendingModifierQueue[0].quantity}
            language={language}
            onConfirm={handleModifiersConfirm}
          />
        )}

        {loading && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </Box>

      {/* ── Inline cart panel ── */}
      <ChatCartPanel
        language={language}
        onCheckout={handleCartCheckout}
      />

      {/* ── Input row ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder={t('chat.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage(input);
            }
          }}
          disabled={loading}
          inputProps={{
            dir: language === 'ar' ? 'rtl' : 'ltr',
            style: { textAlign: language === 'ar' ? 'right' : 'left' },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'divider' },
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
            },
          }}
        />

        {speechSupported && (
          <IconButton
            onClick={startVoiceInput}
            color={isListening ? 'error' : 'default'}
            size="small"
            sx={{
              animation: isListening ? 'micPulse 1s ease-in-out infinite' : 'none',
              '@keyframes micPulse': {
                '0%, 100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0.4)' },
                '50%': { boxShadow: '0 0 0 8px rgba(211,47,47,0)' },
              },
            }}
          >
            {isListening ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
        )}

        <IconButton
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || loading}
          color="primary"
          size="small"
          sx={{
            bgcolor: input.trim() && !loading ? 'primary.main' : 'transparent',
            color: input.trim() && !loading ? 'white' : 'text.disabled',
            '&:hover': {
              bgcolor: input.trim() && !loading ? 'primary.dark' : 'transparent',
            },
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <>
      {/* First-time callout bubble */}
      {!open && showCallout && (
        <Box
          onClick={() => { dismissCallout(); setOpen(true); }}
          sx={{
            position: 'fixed',
            bottom: { xs: 232, sm: 96 },
            right: 16,
            zIndex: theme.zIndex.speedDial,
            maxWidth: 210,
            cursor: 'pointer',
            animation: 'calloutFadeIn 0.35s ease-out',
            '@keyframes calloutFadeIn': {
              from: { opacity: 0, transform: 'translateY(10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
              border: 1,
              borderColor: 'divider',
              p: 1.5,
              pr: 3.5,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -7,
                right: 22,
                width: 0,
                height: 0,
                borderLeft: '7px solid transparent',
                borderRight: '7px solid transparent',
                borderTop: `7px solid ${theme.palette.background.paper}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: -9,
                right: 21,
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: `8px solid ${theme.palette.divider}`,
              },
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); dismissCallout(); }}
              sx={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20 }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>
              {t('chat.callout_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {t('chat.callout_subtitle')}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Floating Action Button */}
      {!open && (
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: { xs: 160, sm: 24 },
            right: 24,
            zIndex: theme.zIndex.speedDial,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            '&:hover': {
              transform: 'scale(1.1)',
              transition: 'transform 0.2s',
            },
          }}
        >
          <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error">
            <SparkleIcon sx={{ fontSize: 26 }} />
          </Badge>
        </Fab>
      )}

      {/* Mobile: fullscreen slide-up dialog */}
      {isMobile ? (
        <Dialog
          fullScreen
          open={open}
          onClose={() => setOpen(false)}
          TransitionComponent={SlideUp}
          sx={{ zIndex: theme.zIndex.modal }}
        >
          {panelContent}
        </Dialog>
      ) : (
        /* Desktop: centered popup dialog — large, proportional to screen */
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="md"
          fullWidth
          TransitionComponent={SlideUp}
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
              maxHeight: '90vh',
              boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
            },
          }}
          sx={{ zIndex: theme.zIndex.modal }}
        >
          {panelContent}
        </Dialog>
      )}
    </>
  );
}
